import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError, type OrderRequest, type Product, type TableInfo } from './api';
import { addLine, cartEstimate, emptyCart, formatMoney, loadCart, saveCart, setQuantity, type CartState } from './cart';
import { CartSheet } from './components/CartSheet';
import { ItemImage } from './components/ItemImage';
import { ProductSheet } from './components/ProductSheet';
import { InvalidTable, Landing } from './components/Landing';
import { StatusView } from './components/StatusView';
import { detectLang, LANGUAGES, translator, type Lang } from './i18n';

// Table links are /t/<token>. Anything else (the bare domain, marketing query strings, other
// paths) is the landing page; a /t/ link with a malformed token gets the recovery page.
function tokenFromPath(): string | null {
  const m = /^\/t\/([A-Za-z0-9_-]{16,100})\/?$/.exec(window.location.pathname);
  return m ? m[1] : null;
}

const isTableLink = () => /^\/t\//.test(window.location.pathname);

function requestFromHash(): string | null {
  const m = /^#\/order\/([0-9a-f-]{36})$/i.exec(window.location.hash);
  return m ? m[1] : null;
}

export function App() {
  const token = tokenFromPath();
  const [lang, setLang] = useState<Lang>(detectLang);
  const t = useMemo(() => translator(lang), [lang]);
  const [info, setInfo] = useState<TableInfo | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [category, setCategory] = useState<string>('all');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Product | null>(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [cart, setCart] = useState<CartState>(() => (token ? loadCart(token) : emptyCart()));
  const [requestId, setRequestId] = useState<string | null>(requestFromHash);
  const [mine, setMine] = useState<OrderRequest[]>([]);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      setInfo(await api.table(token));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError(0, String(e), 'error'));
    }
  }, [token]);

  useEffect(() => {
    void load();
    const timer = setInterval(load, 60_000);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    document.documentElement.lang = lang;
    try {
      localStorage.setItem('qr-lang', lang);
    } catch {
      /* ignore */
    }
  }, [lang]);

  useEffect(() => {
    if (token) saveCart(token, cart);
  }, [token, cart]);

  useEffect(() => {
    const onHash = () => setRequestId(requestFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    if (token && !requestId) api.mine(token).then((r) => setMine(r.requests)).catch(() => {});
  }, [token, requestId]);

  useEffect(() => {
    if (info) document.title = `${info.restaurant.name} · ${t('table', { label: info.table.label })}`;
  }, [info, t]);

  if (!token) return isTableLink() ? <InvalidTable t={t} /> : <Landing t={t} />;
  if (error?.code === 'invalid_token') return <InvalidTable t={t} />;
  if (error) {
    return (
      <div className="center">
        <h1>{t('unavailableTitle')}</h1>
        <p>{error.message}</p>
        <button className="btn secondary" style={{ maxWidth: 240 }} onClick={() => void load()}>{t('retry')}</button>
      </div>
    );
  }
  if (!info) return <div className="center" aria-busy="true">…</div>;

  const menu = info.menu;
  const currency = info.restaurant.currency ?? menu.currency;
  const maxQty = info.ordering.max_quantity_per_item;
  const q = query.trim().toLowerCase();
  const visible = menu.products.filter(
    (p) => (category === 'all' || p.category_id === category) && (!q || p.name.toLowerCase().includes(q) || p.description.toLowerCase().includes(q)),
  );
  const count = cart.lines.reduce((s, l) => s + l.quantity, 0);

  const header = (
    <header className="header">
      <div className="sub">
        <h1>{info.restaurant.name}</h1>
        <select className="lang" aria-label={t('language')} value={lang} onChange={(e) => setLang(e.target.value as Lang)}>
          {Object.entries(LANGUAGES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <div className="sub"><span>{t('table', { label: info.table.label })}</span></div>
    </header>
  );

  if (requestId) {
    return (
      <div className="app">
        {header}
        <StatusView token={token} requestId={requestId} info={info} t={t} onBack={() => (window.location.hash = '')} />
      </div>
    );
  }

  const submit = async () => {
    const res = await api.submit(token, {
      client_request_id: cart.requestId,
      items: cart.lines,
      note: cart.note,
      customer_name: cart.detailsSkipped ? '' : cart.name.trim(),
      customer_phone: cart.detailsSkipped ? '' : cart.phone.trim(),
      wa_updates_consent: !cart.detailsSkipped && Boolean(cart.phone.trim()) && cart.waUpdates,
    });
    setCart({ ...emptyCart(), name: cart.name, phone: cart.phone, waUpdates: cart.waUpdates, detailsSkipped: cart.detailsSkipped });
    setCartOpen(false);
    window.location.hash = `#/order/${res.request.id}`;
  };

  return (
    <div className="app">
      {header}
      <div style={{ padding: '0 16px', background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
        <input className="search" type="search" placeholder={t('search')} aria-label={t('search')} value={query} onChange={(e) => setQuery(e.target.value)} />
        <div className="chips" role="toolbar">
          <button className="chip" aria-pressed={category === 'all'} onClick={() => setCategory('all')}>{t('all')}</button>
          {menu.categories.map((c) => (
            <button key={c.id} className="chip" aria-pressed={category === c.id} onClick={() => setCategory(c.id)}>{c.name}</button>
          ))}
        </div>
      </div>
      {info.ordering.welcome_message && <p className="banner info">{info.ordering.welcome_message}</p>}
      {!info.online && <p className="banner" role="status">{t('restaurantOffline')}</p>}
      {mine.some((r) => !['completed', 'cancelled', 'expired', 'rejected'].includes(r.status)) && (
        <div className="card">
          <strong>{t('myOrders')}</strong>
          {mine.slice(0, 5).map((r) => (
            <div key={r.id} className="line">
              <span className="meta">{r.order_number ? t('orderNumber', { n: r.order_number }) : new Date(r.created_at).toLocaleTimeString()}</span>
              <a href={`#/order/${r.id}`}>{t(`status_${r.status}` as Parameters<typeof t>[0])}</a>
            </div>
          ))}
        </div>
      )}

      <main>
        {menu.categories
          .filter((c) => visible.some((p) => p.category_id === c.id))
          .map((c) => (
            <section key={c.id} aria-labelledby={`cat-${c.id}`}>
              <h2 className="section-title" id={`cat-${c.id}`}>{c.name}</h2>
              {visible
                .filter((p) => p.category_id === c.id)
                .map((p) => (
                  <button key={p.id} className="product" aria-disabled={!p.available} onClick={() => p.available && setSelected(p)}>
                    <div className="info">
                      <div className="name">{p.name}</div>
                      {p.description && <div className="desc">{p.description}</div>}
                      {(p.dietary_tags.length > 0 || !p.available) && (
                        <div className="tags">
                          {!p.available && <span className="tag">{t('soldOut')}</span>}
                          {p.dietary_tags.map((tag) => <span className="tag" key={tag}>{tag}</span>)}
                        </div>
                      )}
                      <div className="price">{formatMoney(p.variants.length ? Math.min(...p.variants.map((v) => v.price)) : p.price, currency)}</div>
                    </div>
                    <ItemImage src={p.has_image ? api.imageUrl(token, p.id) : null} />
                  </button>
                ))}
            </section>
          ))}
      </main>

      {count > 0 && (
        <div className="bar">
          <div className="inner">
            <button className="btn" onClick={() => setCartOpen(true)}>
              {t('viewCart', { count })} · {formatMoney(cartEstimate(menu, cart.lines), currency)}
            </button>
          </div>
        </div>
      )}

      {selected && (
        <ProductSheet
          product={selected}
          currency={currency}
          maxQty={maxQty}
          imageUrl={selected.has_image ? api.imageUrl(token, selected.id) : null}
          t={t}
          onClose={() => setSelected(null)}
          onAdd={(line) => {
            setCart((c) => addLine(c, line, maxQty));
            setSelected(null);
          }}
        />
      )}
      {cartOpen && (
        <CartSheet
          token={token}
          info={info}
          menu={menu}
          cart={cart}
          t={t}
          onChange={setCart}
          onQuantity={(key, n) => setCart((c) => setQuantity(c, key, n, maxQty))}
          onSubmit={submit}
          onClose={() => setCartOpen(false)}
        />
      )}
    </div>
  );
}
